/**
 * Cart tools — cálculo de frete por CEP e aplicação de cupons.
 * Usado no cart drawer (snippets/halo-side-cart.liquid) e na página do carrinho
 * (sections/main-cart.liquid) via os snippets cart-shipping / cart-discount.
 *
 * Textos vêm de window.cartToolsStrings (snippets/global-script.liquid).
 */
(() => {
  const STRINGS = window.cartToolsStrings || {};
  const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const STORAGE_CEP = 'cartTools:cep';
  const STORAGE_RATE = 'cartTools:rate';
  /** Mantem header e carrinho em sincronia na mesma pagina. */
  const CEP_EVENT = 'cartTools:cep-changed';

  const t = (key, fallback) => STRINGS[key] || fallback;

  const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

  /** 38411066 -> 38411-066. Valores parciais voltam sem o hifen. */
  const formatCep = (value) => {
    const digits = onlyDigits(value).slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const readSavedCep = () => onlyDigits(localStorage.getItem(STORAGE_CEP));

  /**
   * Avisa os outros componentes que o CEP salvo mudou.
   * @param {string} zip CEP com 8 digitos, ou '' quando o cliente removeu.
   * @param {HTMLElement} source Quem originou, para nao reagir ao proprio evento.
   */
  const emitCepChange = (zip, source) => {
    document.dispatchEvent(new CustomEvent(CEP_EVENT, { detail: { zip, source } }));
  };

  const cartRoute = () => window.routes?.cart || '/cart';
  const cartUpdateRoute = () => window.routes?.cart_update_url || '/cart/update.js';

  /** Na página do carrinho não há re-render por AJAX, então recarregamos. */
  const isCartPage = () =>
    window.location.pathname.replace(/\/$/, '') === cartRoute().replace(/\/$/, '');

  /**
   * Reflete no layout o carrinho recém-atualizado: no drawer o tema já sabe
   * re-renderizar; na página do carrinho só um reload atualiza os totais.
   * @param {Object} cart Objeto de carrinho retornado pela Ajax Cart API.
   */
  const syncCartUI = (cart) => {
    if (isCartPage()) {
      window.location.reload();
      return;
    }

    if (typeof window.sharedFunctions?.updateSidebarCart === 'function') {
      window.sharedFunctions.updateSidebarCart(cart);
    }
  };

  const getCart = () =>
    fetch(`${cartRoute()}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /*
   * Faixas de CEP por UF (tabela dos Correios). O endpoint de frete da Shopify
   * exige a província junto do CEP — sem ela apps de frete BR devolvem lista vazia.
   */
  const CEP_RANGES = [
    [1000, 19999, 'SP'],
    [20000, 28999, 'RJ'],
    [29000, 29999, 'ES'],
    [30000, 39999, 'MG'],
    [40000, 48999, 'BA'],
    [49000, 49999, 'SE'],
    [50000, 56999, 'PE'],
    [57000, 57999, 'AL'],
    [58000, 58999, 'PB'],
    [59000, 59999, 'RN'],
    [60000, 63999, 'CE'],
    [64000, 64999, 'PI'],
    [65000, 65999, 'MA'],
    [66000, 68899, 'PA'],
    [68900, 68999, 'AP'],
    [69000, 69299, 'AM'],
    [69300, 69399, 'RR'],
    [69400, 69899, 'AM'],
    [69900, 69999, 'AC'],
    [70000, 72799, 'DF'],
    [72800, 72999, 'GO'],
    [73000, 73699, 'DF'],
    [73700, 76799, 'GO'],
    [76800, 76999, 'RO'],
    [77000, 77999, 'TO'],
    [78000, 78899, 'MT'],
    [78900, 78999, 'RO'],
    [79000, 79999, 'MS'],
    [80000, 87999, 'PR'],
    [88000, 89999, 'SC'],
    [90000, 99999, 'RS'],
  ];

  const provinceFromCep = (zip) => {
    const prefix = Number(zip.slice(0, 5));
    const range = CEP_RANGES.find(([min, max]) => prefix >= min && prefix <= max);
    return range ? range[2] : '';
  };

  // shipping_rates.json devolve o preço já em reais ("25.90"), não em centavos.
  const formatMoney = (price) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      Number(price) || 0
    );

  /**
   * Prazo informado pela transportadora: delivery_days vem em dias e
   * delivery_range em datas ISO — usamos o limite superior do que existir.
   * @param {Object} rate Tarifa de shipping_rates.json.
   * @returns {number|null} Dias até a entrega, ou null se a transportadora não informar.
   */
  const deliveryDays = (rate) => {
    const days = Array.isArray(rate.delivery_days) ? rate.delivery_days : [];
    if (days.length) return Number(days[days.length - 1]) || null;

    const range = Array.isArray(rate.delivery_range) ? rate.delivery_range : [];
    const last = range[range.length - 1];
    if (!last) return null;

    const diff = Math.ceil((new Date(last).getTime() - Date.now()) / 86400000);
    return Number.isFinite(diff) && diff > 0 ? diff : null;
  };

  const escapeHtml = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (char) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );

  /**
   * Base comum: linha "label + botão que abre o campo inline", com estado de
   * aberto/fechado em data-open, spinner no submit e parágrafo de erro.
   */
  class CartToolElement extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.cart-tool__form');
      this.input = this.querySelector('.cart-tool__input');
      this.toggleButton = this.querySelector('.cart-tool__toggle');
      this.toggleLabel = this.querySelector('.cart-tool__toggle-text');
      this.errorEl = this.querySelector('.cart-tool__error');
      this.submitButton = this.querySelector('.cart-tool__submit');

      this.onSubmit = this.onSubmit.bind(this);
      this.onToggle = this.onToggle.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);
      this.toggleButton?.addEventListener('click', this.onToggle);

      this.setup?.();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.toggleButton?.removeEventListener('click', this.onToggle);
    }

    get openLabel() {
      return this.dataset.openLabel || '';
    }

    setOpen(open) {
      this.dataset.open = open ? 'opened' : 'closed';
      if (this.toggleLabel) {
        this.toggleLabel.textContent = open ? t('close', 'Fechar') : this.openLabel;
      }
      if (open) this.input?.focus();
    }

    onToggle(event) {
      event.preventDefault();
      const open = this.dataset.open !== 'opened';
      this.setOpen(open);
      this.showError('');
      if (this.input) this.input.value = '';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      this.submitButton.disabled = loading;
    }
  }

  class CartShipping extends CartToolElement {
    setup() {
      this.resultEl = this.querySelector('.cart-tool__result');

      this.onCepChange = this.onCepChange.bind(this);
      document.addEventListener(CEP_EVENT, this.onCepChange);

      const savedZip = readSavedCep();
      const savedRate = localStorage.getItem(STORAGE_RATE);
      if (savedZip.length !== 8) return;

      // O CEP pode ter vindo do header, antes de existir carrinho: o campo já
      // aparece preenchido quando o cliente abre o carrinho.
      if (this.input) this.input.value = formatCep(savedZip);

      // Pinta a última cotação de imediato e revalida em segundo plano, porque
      // o valor muda quando o conteúdo do carrinho muda.
      if (savedRate) {
        try {
          this.renderRate(JSON.parse(savedRate));
        } catch (error) {
          localStorage.removeItem(STORAGE_RATE);
        }
      }
      this.calculate(savedZip, { silent: true });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener(CEP_EVENT, this.onCepChange);
    }

    /** CEP alterado em outro componente (o "Enviar para" do header). */
    onCepChange(event) {
      if (event.detail?.source === this) return;

      const zip = onlyDigits(event.detail?.zip);

      if (zip.length !== 8) {
        if (this.input) this.input.value = '';
        this.clearRate();
        return;
      }

      /*
       * Na página do carrinho existem duas instâncias (drawer e página). Se as
       * duas reagissem a um CEP que já é o delas, cada eco geraria uma nova
       * cotação — ida e volta sem fim.
       */
      if (onlyDigits(this.input?.value) === zip) return;

      if (this.input) this.input.value = formatCep(zip);
      this.showError('');
      this.calculate(zip, { silent: true });
    }

    async onSubmit(event) {
      event.preventDefault();
      const zip = (this.input?.value || '').replace(/\D/g, '');

      if (zip.length !== 8) {
        this.showError(t('cep_invalid', 'Informe um CEP válido (8 dígitos).'));
        return;
      }

      this.showError('');
      await this.calculate(zip);
    }

    /**
     * Cotação em duas etapas exigida pela Shopify: prepare_shipping_rates dispara
     * o cálculo e shipping_rates.json é consultado até os apps responderem.
     * @param {string} zip CEP com 8 dígitos.
     * @param {{silent?: boolean}} options silent evita spinner/erro no revalidar.
     */
    async calculate(zip, { silent = false } = {}) {
      const province = provinceFromCep(zip);
      const address = {
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': province,
      };

      if (!silent) this.setLoading(true);

      try {
        const prepare = await fetch(`${cartRoute()}/prepare_shipping_rates.json`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({
            shipping_address: { zip, country: 'Brazil', province },
          }),
        });
        if (!prepare.ok) throw new Error('prepare failed');

        const rates = await this.pollRates(
          `${cartRoute()}/shipping_rates.json?${new URLSearchParams(address)}`
        );

        if (!rates.length) {
          // Sem tarifa não podemos manter o valor antigo em tela, mesmo revalidando.
          this.clearRate();
          if (!silent) {
            this.showError(t('shipping_rates_empty', 'Nenhuma opção de frete para este CEP.'));
          }
          return;
        }

        rates.sort((a, b) => a.price - b.price);
        localStorage.setItem(STORAGE_CEP, zip);
        localStorage.setItem(STORAGE_RATE, JSON.stringify(rates[0]));
        this.renderRate(rates[0]);

        if (!silent) {
          this.setOpen(false);
          // Espelha no header: quem digitou aqui também vê "Enviar para: <CEP>".
          // Só no fluxo digitado — revalidação silenciosa não avisa ninguém,
          // senão duas instâncias ficariam recotando uma por causa da outra.
          emitCepChange(zip, this);
        }
      } catch (error) {
        if (!silent) {
          this.showError(
            t('shipping_rates_error', 'Não foi possível calcular o frete. Tente novamente.')
          );
        }
      } finally {
        if (!silent) this.setLoading(false);
      }
    }

    /** As tarifas ficam prontas de forma assíncrona; consultamos até chegarem. */
    async pollRates(url, { tries = 12, delay = 500 } = {}) {
      for (let attempt = 0; attempt < tries; attempt += 1) {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) {
            return data.shipping_rates;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      return [];
    }

    renderRate(rate) {
      if (!this.resultEl || !rate) return;

      const days = deliveryDays(rate);
      const deadline = days
        ? t('delivery_days', 'em até [count] dias úteis').replace('[count]', days)
        : '';
      const price =
        Number(rate.price) === 0 ? t('shipping_free', 'Grátis') : formatMoney(rate.price);

      this.resultEl.innerHTML = `
        <div class="cart-tool__result-item">
          <span class="cart-tool__result-name">${escapeHtml(rate.name)}${
            deadline ? ` <span class="cart-tool__result-deadline">${escapeHtml(deadline)}</span>` : ''
          }</span>
          <span class="cart-tool__result-price">${escapeHtml(price)}</span>
          <button type="button" class="cart-tool__result-remove" data-clear-rate aria-label="${escapeHtml(
            t('shipping_recalculate', 'Refazer cálculo')
          )}">&#10005;</button>
        </div>
      `;
      this.resultEl.hidden = false;
      this.classList.add('cart-tool--applied');

      this.resultEl
        .querySelector('[data-clear-rate]')
        ?.addEventListener('click', () => this.clearRate({ reopen: true }));
    }

    clearRate({ reopen = false } = {}) {
      const hadZip = readSavedCep().length === 8;
      localStorage.removeItem(STORAGE_CEP);
      localStorage.removeItem(STORAGE_RATE);
      if (hadZip) emitCepChange('', this);

      if (this.resultEl) {
        this.resultEl.innerHTML = '';
        this.resultEl.hidden = true;
      }
      this.classList.remove('cart-tool--applied');
      if (reopen) this.setOpen(true);
    }
  }

  class CartDiscount extends CartToolElement {
    setup() {
      this.listEl = this.querySelector('.cart-tool__tags');
      this.codes = [];

      this.onListClick = this.onListClick.bind(this);
      this.listEl?.addEventListener('click', this.onListClick);

      this.syncFromCart();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.listEl?.removeEventListener('click', this.onListClick);
    }

    async syncFromCart() {
      try {
        const cart = await getCart();
        this.codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        this.renderTags();
      } catch (error) {
        this.codes = [];
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      const code = (this.input?.value || '').trim();

      if (!code) {
        this.showError(t('coupon_empty', 'Digite um código de cupom.'));
        return;
      }
      if (this.codes.some((discount) => discount.code.toLowerCase() === code.toLowerCase())) {
        this.showError(t('coupon_duplicated', 'Este cupom já foi aplicado.'));
        return;
      }

      await this.updateCodes([...this.codes.map((discount) => discount.code), code]);
    }

    onListClick(event) {
      const removeAll = event.target.closest('[data-remove-all]');
      if (removeAll) {
        this.updateCodes([]);
        return;
      }

      const removeOne = event.target.closest('[data-remove-code]');
      if (removeOne) {
        const code = removeOne.dataset.removeCode;
        this.updateCodes(
          this.codes.map((discount) => discount.code).filter((current) => current !== code)
        );
      }
    }

    /**
     * A Ajax Cart API substitui todos os cupons de uma vez, então sempre
     * enviamos a lista completa desejada (vazia remove todos).
     * @param {string[]} codes Códigos que devem ficar aplicados.
     */
    async updateCodes(codes) {
      this.setLoading(true);
      this.showError('');

      try {
        const response = await fetch(cartUpdateRoute(), {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        const cart = await response.json();

        if (!response.ok) {
          this.showError(
            cart?.description || t('coupon_error', 'Não foi possível aplicar o cupom. Tente novamente.')
          );
          return;
        }

        this.codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        if (this.input) this.input.value = '';
        this.renderTags();
        this.setOpen(false);
        syncCartUI(cart);
      } catch (error) {
        this.showError(t('coupon_error', 'Não foi possível aplicar o cupom. Tente novamente.'));
      } finally {
        this.setLoading(false);
      }
    }

    renderTags() {
      if (!this.listEl) return;

      if (!this.codes.length) {
        this.listEl.innerHTML = '';
        this.listEl.hidden = true;
        this.classList.remove('cart-tool--applied');
        return;
      }

      const tags = this.codes
        .map(
          (discount) => `
            <li class="cart-tool__tag${
              discount.applicable ? '' : ' cart-tool__tag--invalid'
            }">
              <span class="cart-tool__tag-code">${escapeHtml(discount.code)}</span>
              ${
                discount.applicable
                  ? ''
                  : `<span class="cart-tool__tag-status">${escapeHtml(
                      t('coupon_not_applicable', 'Não aplicável')
                    )}</span>`
              }
              <button type="button" data-remove-code="${escapeHtml(
                discount.code
              )}" aria-label="${escapeHtml(t('remove_coupon', 'Remover cupom'))}">&#10005;</button>
            </li>`
        )
        .join('');

      this.listEl.innerHTML = `
        <ul class="cart-tool__tag-list">${tags}</ul>
        <div class="cart-tool__tag-actions">
          <span>${escapeHtml(
            t('coupon_applied_count', 'Cupons aplicados ([count])').replace(
              '[count]',
              this.codes.length
            )
          )}</span>
          <button type="button" data-remove-all>${escapeHtml(
            t('remove_all', 'Remover todos')
          )}</button>
        </div>
      `;
      this.listEl.hidden = false;
      this.classList.add('cart-tool--applied');
    }
  }

  /**
   * "Enviar para: <CEP>" no header (snippets/header-shipping-cep.liquid).
   *
   * Não cota frete: guarda o CEP na mesma chave do carrinho (STORAGE_CEP), que
   * é o que faz o cálculo aparecer preenchido depois. A cotação salva é
   * descartada ao trocar de CEP, senão o carrinho pintaria por um instante o
   * valor do destino anterior.
   */
  class HeaderCep extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-cep-trigger]');
      this.valueEl = this.querySelector('[data-cep-value]');
      /*
       * popoverEl, nao popover: `popover` e propriedade nativa do HTMLElement
       * (Popover API) e o setter converteria o elemento em string.
       */
      this.popoverEl = this.querySelector('[data-cep-popover]');
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-cep-input]');
      this.submitButton = this.querySelector('[data-cep-submit]');
      this.errorEl = this.querySelector('[data-cep-error]');

      this.onTrigger = this.onTrigger.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCepChange = this.onCepChange.bind(this);

      this.trigger?.addEventListener('click', this.onTrigger);
      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', this.onInput);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener(CEP_EVENT, this.onCepChange);

      this.renderValue(readSavedCep());
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.onTrigger);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener(CEP_EVENT, this.onCepChange);
    }

    get emptyLabel() {
      return this.dataset.emptyLabel || '';
    }

    get isOpen() {
      return this.dataset.open === 'opened';
    }

    renderValue(zip) {
      const filled = zip.length === 8;
      if (this.valueEl) {
        this.valueEl.textContent = filled ? formatCep(zip) : this.emptyLabel;
      }
      this.classList.toggle('header-cep--filled', filled);
    }

    setOpen(open) {
      this.dataset.open = open ? 'opened' : 'closed';
      this.trigger?.setAttribute('aria-expanded', String(open));
      if (this.popoverEl) this.popoverEl.hidden = !open;
      this.showError('');

      if (!open || !this.input) return;

      // Abre com o CEP atual em tela: trocar de destino é o caso comum aqui.
      this.input.value = formatCep(readSavedCep());
      this.syncSubmitState();
      this.input.focus();
      this.input.select();
    }

    onTrigger(event) {
      event.preventDefault();
      this.setOpen(!this.isOpen);
    }

    onInput() {
      if (!this.input) return;
      this.input.value = formatCep(this.input.value);
      this.showError('');
      this.syncSubmitState();
    }

    syncSubmitState() {
      if (!this.submitButton) return;
      this.submitButton.disabled = onlyDigits(this.input?.value).length !== 8;
    }

    onSubmit(event) {
      event.preventDefault();
      const zip = onlyDigits(this.input?.value);

      if (zip.length !== 8) {
        this.showError(t('cep_invalid', 'Informe um CEP válido (8 dígitos).'));
        return;
      }

      localStorage.setItem(STORAGE_CEP, zip);
      localStorage.removeItem(STORAGE_RATE);

      this.renderValue(zip);
      this.setOpen(false);
      emitCepChange(zip, this);
    }

    onDocumentClick(event) {
      if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.setOpen(false);
      this.trigger?.focus();
    }

    /** CEP alterado no carrinho ou na outra instância (desktop/mobile). */
    onCepChange(event) {
      if (event.detail?.source === this) return;
      this.renderValue(onlyDigits(event.detail?.zip));
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  if (!customElements.get('cart-shipping-component')) {
    customElements.define('cart-shipping-component', CartShipping);
  }
  if (!customElements.get('header-cep-component')) {
    customElements.define('header-cep-component', HeaderCep);
  }
  if (!customElements.get('cart-discount-component')) {
    customElements.define('cart-discount-component', CartDiscount);
  }
})();
